"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkValueTypeMatch = exports.checkTypeMatch = void 0;
const assignment_cache_1 = require("../assignment-cache");
const constants_1 = require("../constants");
const decoding_1 = require("../decoding");
const eppo_value_1 = require("../eppo_value");
const evaluator_1 = require("../evaluator");
const flag_configuration_requestor_1 = require("../flag-configuration-requestor");
const http_client_1 = require("../http-client");
const interfaces_1 = require("../interfaces");
const obfuscation_1 = require("../obfuscation");
const poller_1 = require("../poller");
const validation_1 = require("../validation");
const version_1 = require("../version");
class EppoClient {
    constructor(configurationStore, configurationRequestParameters, obfuscated = false) {
        this.queuedEvents = [];
        this.isGracefulFailureMode = true;
        this.isObfuscated = false;
        this.evaluator = new evaluator_1.Evaluator();
        this.configurationStore = configurationStore;
        this.configurationRequestParameters = configurationRequestParameters;
        this.isObfuscated = obfuscated;
    }
    setConfigurationRequestParameters(configurationRequestParameters) {
        this.configurationRequestParameters = configurationRequestParameters;
    }
    async fetchFlagConfigurations() {
        var _a, _b, _c, _d, _e;
        if (!this.configurationRequestParameters) {
            throw new Error('Eppo SDK unable to fetch flag configurations without configuration request parameters');
        }
        if (this.requestPoller) {
            // if fetchFlagConfigurations() was previously called, stop any polling process from that call
            this.requestPoller.stop();
        }
        // todo: consider injecting the IHttpClient interface
        const httpClient = new http_client_1.default(this.configurationRequestParameters.baseUrl || constants_1.BASE_URL, {
            apiKey: this.configurationRequestParameters.apiKey,
            sdkName: this.configurationRequestParameters.sdkName,
            sdkVersion: this.configurationRequestParameters.sdkVersion,
        }, this.configurationRequestParameters.requestTimeoutMs || constants_1.DEFAULT_REQUEST_TIMEOUT_MS);
        const configurationRequestor = new flag_configuration_requestor_1.default(this.configurationStore, httpClient);
        this.requestPoller = (0, poller_1.default)(constants_1.POLL_INTERVAL_MS, configurationRequestor.fetchAndStoreConfigurations.bind(configurationRequestor), {
            maxStartRetries: (_a = this.configurationRequestParameters.numInitialRequestRetries) !== null && _a !== void 0 ? _a : constants_1.DEFAULT_INITIAL_CONFIG_REQUEST_RETRIES,
            maxPollRetries: (_b = this.configurationRequestParameters.numPollRequestRetries) !== null && _b !== void 0 ? _b : constants_1.DEFAULT_POLL_CONFIG_REQUEST_RETRIES,
            pollAfterSuccessfulStart: (_c = this.configurationRequestParameters.pollAfterSuccessfulInitialization) !== null && _c !== void 0 ? _c : false,
            pollAfterFailedStart: (_d = this.configurationRequestParameters.pollAfterFailedInitialization) !== null && _d !== void 0 ? _d : false,
            errorOnFailedStart: (_e = this.configurationRequestParameters.throwOnFailedInitialization) !== null && _e !== void 0 ? _e : false,
        });
        await this.requestPoller.start();
    }
    stopPolling() {
        if (this.requestPoller) {
            this.requestPoller.stop();
        }
    }
    getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        var _a;
        return ((_a = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.String(defaultValue), interfaces_1.VariationType.STRING).stringValue) !== null && _a !== void 0 ? _a : defaultValue);
    }
    getBoolAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        var _a;
        return ((_a = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.Bool(defaultValue), interfaces_1.VariationType.BOOLEAN).boolValue) !== null && _a !== void 0 ? _a : defaultValue);
    }
    getIntegerAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        var _a;
        return ((_a = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.Numeric(defaultValue), interfaces_1.VariationType.INTEGER).numericValue) !== null && _a !== void 0 ? _a : defaultValue);
    }
    getNumericAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        var _a;
        return ((_a = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.Numeric(defaultValue), interfaces_1.VariationType.NUMERIC).numericValue) !== null && _a !== void 0 ? _a : defaultValue);
    }
    getJSONAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        var _a;
        return ((_a = this.getAssignmentVariation(flagKey, subjectKey, subjectAttributes, eppo_value_1.EppoValue.JSON(defaultValue), interfaces_1.VariationType.JSON).objectValue) !== null && _a !== void 0 ? _a : defaultValue);
    }
    getAssignmentVariation(flagKey, subjectKey, subjectAttributes, defaultValue, expectedVariationType) {
        try {
            const result = this.getAssignmentDetail(flagKey, subjectKey, subjectAttributes, expectedVariationType);
            if (!result.variation) {
                return defaultValue;
            }
            return eppo_value_1.EppoValue.valueOf(result.variation.value, expectedVariationType);
        }
        catch (error) {
            return this.rethrowIfNotGraceful(error, defaultValue);
        }
    }
    rethrowIfNotGraceful(err, defaultValue) {
        if (this.isGracefulFailureMode) {
            console.error(`[Eppo SDK] Error getting assignment: ${err.message}`);
            return defaultValue !== null && defaultValue !== void 0 ? defaultValue : eppo_value_1.EppoValue.Null();
        }
        throw err;
    }
    /**
     * [Experimental] Get a detailed return of assignment for a particular subject and flag.
     *
     * Note: This method is experimental and may change in future versions.
     * Please only use for debugging purposes, and not in production.
     *
     * @param subjectKey The subject key
     * @param flagKey The flag key
     * @param subjectAttributes The subject attributes
     * @param expectedVariationType The expected variation type
     * @param obfuscated Whether the flag key is obfuscated
     * @returns A detailed return of assignment for a particular subject and flag
     */
    getAssignmentDetail(flagKey, subjectKey, subjectAttributes = {}, expectedVariationType) {
        (0, validation_1.validateNotBlank)(subjectKey, 'Invalid argument: subjectKey cannot be blank');
        (0, validation_1.validateNotBlank)(flagKey, 'Invalid argument: flagKey cannot be blank');
        const flag = this.getFlag(flagKey);
        if (flag === null) {
            console.warn(`[Eppo SDK] No assigned variation. Flag not found: ${flagKey}`);
            // note: this is different from the Python SDK, which returns None instead
            return (0, evaluator_1.noneResult)(flagKey, subjectKey, subjectAttributes);
        }
        if (!checkTypeMatch(expectedVariationType, flag.variationType)) {
            throw new TypeError(`Variation value does not have the correct type. Found: ${flag.variationType} != ${expectedVariationType} for flag ${flagKey}`);
        }
        if (!flag.enabled) {
            console.info(`[Eppo SDK] No assigned variation. Flag is disabled: ${flagKey}`);
            // note: this is different from the Python SDK, which returns None instead
            return (0, evaluator_1.noneResult)(flagKey, subjectKey, subjectAttributes);
        }
        const result = this.evaluator.evaluateFlag(flag, subjectKey, subjectAttributes, this.isObfuscated);
        if (this.isObfuscated) {
            // flag.key is obfuscated, replace with requested flag key
            result.flagKey = flagKey;
        }
        if ((result === null || result === void 0 ? void 0 : result.variation) && !checkValueTypeMatch(expectedVariationType, result.variation.value)) {
            return (0, evaluator_1.noneResult)(flagKey, subjectKey, subjectAttributes);
        }
        try {
            if (result === null || result === void 0 ? void 0 : result.doLog) {
                this.logAssignment(result);
            }
        }
        catch (error) {
            console.error(`[Eppo SDK] Error logging assignment event: ${error}`);
        }
        return result;
    }
    getFlag(flagKey) {
        if (this.isObfuscated) {
            return this.getObfuscatedFlag(flagKey);
        }
        return this.configurationStore.get(flagKey);
    }
    getObfuscatedFlag(flagKey) {
        const flag = this.configurationStore.get((0, obfuscation_1.getMD5Hash)(flagKey));
        return flag ? (0, decoding_1.decodeFlag)(flag) : null;
    }
    getFlagKeys() {
        /**
         * Returns a list of all flag keys that have been initialized.
         * This can be useful to debug the initialization process.
         *
         * Note that it is generally not a good idea to pre-load all flag configurations.
         */
        return this.configurationStore.getKeys();
    }
    isInitialized() {
        return this.configurationStore.isInitialized();
    }
    setLogger(logger) {
        this.assignmentLogger = logger;
        this.flushQueuedEvents(); // log any events that may have been queued while initializing
    }
    /**
     * Assignment cache methods.
     */
    disableAssignmentCache() {
        this.assignmentCache = undefined;
    }
    useNonExpiringInMemoryAssignmentCache() {
        this.assignmentCache = new assignment_cache_1.NonExpiringInMemoryAssignmentCache();
    }
    useLRUInMemoryAssignmentCache(maxSize) {
        this.assignmentCache = new assignment_cache_1.LRUInMemoryAssignmentCache(maxSize);
    }
    useCustomAssignmentCache(cache) {
        this.assignmentCache = cache;
    }
    setIsGracefulFailureMode(gracefulFailureMode) {
        this.isGracefulFailureMode = gracefulFailureMode;
    }
    flushQueuedEvents() {
        var _a;
        const eventsToFlush = this.queuedEvents;
        this.queuedEvents = [];
        try {
            for (const event of eventsToFlush) {
                (_a = this.assignmentLogger) === null || _a === void 0 ? void 0 : _a.logAssignment(event);
            }
        }
        catch (error) {
            console.error(`[Eppo SDK] Error flushing assignment events: ${error.message}`);
        }
    }
    logAssignment(result) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const event = Object.assign(Object.assign({}, ((_a = result.extraLogging) !== null && _a !== void 0 ? _a : {})), { allocation: (_b = result.allocationKey) !== null && _b !== void 0 ? _b : null, experiment: result.allocationKey ? `${result.flagKey}-${result.allocationKey}` : null, featureFlag: result.flagKey, variation: (_d = (_c = result.variation) === null || _c === void 0 ? void 0 : _c.key) !== null && _d !== void 0 ? _d : null, subject: result.subjectKey, timestamp: new Date().toISOString(), subjectAttributes: result.subjectAttributes, metaData: {
                obfuscated: this.isObfuscated,
                sdkLanguage: 'javascript',
                sdkLibVersion: version_1.LIB_VERSION,
            } });
        if (result.variation &&
            result.allocationKey &&
            ((_e = this.assignmentCache) === null || _e === void 0 ? void 0 : _e.hasLoggedAssignment({
                flagKey: result.flagKey,
                subjectKey: result.subjectKey,
                allocationKey: result.allocationKey,
                variationKey: result.variation.key,
            }))) {
            return;
        }
        // assignment logger may be null while waiting for initialization
        if (this.assignmentLogger == null) {
            this.queuedEvents.length < constants_1.MAX_EVENT_QUEUE_SIZE && this.queuedEvents.push(event);
            return;
        }
        try {
            this.assignmentLogger.logAssignment(event);
            (_f = this.assignmentCache) === null || _f === void 0 ? void 0 : _f.setLastLoggedAssignment({
                flagKey: result.flagKey,
                subjectKey: result.subjectKey,
                allocationKey: (_g = result.allocationKey) !== null && _g !== void 0 ? _g : '__eppo_no_allocation',
                variationKey: (_j = (_h = result.variation) === null || _h === void 0 ? void 0 : _h.key) !== null && _j !== void 0 ? _j : '__eppo_no_variation',
            });
        }
        catch (error) {
            console.error(`[Eppo SDK] Error logging assignment event: ${error.message}`);
        }
    }
}
exports.default = EppoClient;
function checkTypeMatch(expectedType, actualType) {
    return expectedType === undefined || actualType === expectedType;
}
exports.checkTypeMatch = checkTypeMatch;
function checkValueTypeMatch(expectedType, value) {
    if (expectedType == undefined) {
        return true;
    }
    switch (expectedType) {
        case interfaces_1.VariationType.STRING:
            return typeof value === 'string';
        case interfaces_1.VariationType.BOOLEAN:
            return typeof value === 'boolean';
        case interfaces_1.VariationType.INTEGER:
            return typeof value === 'number' && Number.isInteger(value);
        case interfaces_1.VariationType.NUMERIC:
            return typeof value === 'number';
        case interfaces_1.VariationType.JSON:
            // note: converting to object downstream
            return typeof value === 'string';
        default:
            return false;
    }
}
exports.checkValueTypeMatch = checkValueTypeMatch;
//# sourceMappingURL=eppo-client.js.map